import { Box } from '@talos/kyoko';
import type { AgCartesianChartOptions } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { formatCostRiskBps, formatDurationMinutes } from '../../../utils';
import { getSignedBandRange, type SnipeSweepBand } from './utils';

type AxesOptions = NonNullable<AgCartesianChartOptions['axes']>;
type CrossLine = NonNullable<NonNullable<AxesOptions['y']>['crossLines']>[number];

const ChartWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;

  > div {
    position: absolute;
    inset: 0;
  }
`;

interface RangeDatum {
  x: number;
  low: number;
  high: number;
}

export interface SnipeSweepBoundsChartProps {
  bands: SnipeSweepBand[];
  /** Optional fixed max for the x-axis. Defaults to the longest band's duration. */
  maxDurationMinutes?: number;
  /** Optional fixed |y| range. Defaults to the largest absolute bound across bands. */
  maxAbsBound?: number;
}

export function SnipeSweepBoundsChart({ bands, maxDurationMinutes, maxAbsBound }: SnipeSweepBoundsChartProps) {
  const theme = useTheme();

  const chartOptions = useMemo((): AgCartesianChartOptions => {
    const xMax = maxDurationMinutes ?? bands.reduce((acc, b) => Math.max(acc, b.durationMinutes), 0);

    const ranges = bands.map(b => ({ band: b, range: getSignedBandRange(b) }));

    const yAbs =
      maxAbsBound ?? ranges.reduce((acc, { range }) => Math.max(acc, Math.abs(range.high), Math.abs(range.low)), 0);
    const yPad = Math.max(yAbs * 0.2, 0.5);

    const titleStyle = {
      color: theme.colorTextDefault,
      fontSize: 11,
      fontFamily: theme.fontFamily,
    };

    const series: NonNullable<AgCartesianChartOptions['series']> = ranges.map(({ band, range }) => {
      const data: RangeDatum[] = [
        { x: 0, low: range.low, high: range.high },
        { x: band.durationMinutes, low: range.low, high: range.high },
      ];
      return {
        type: 'range-area' as const,
        data,
        xKey: 'x',
        yLowKey: 'low',
        yHighKey: 'high',
        fill: band.color,
        fillOpacity: 0.22,
        stroke: band.color,
        strokeWidth: 1.5,
        lineDash: [6, 4],
        marker: { enabled: false },
        tooltip: {
          renderer: () => ({
            heading: band.label,
            data: [
              { label: 'Snipe', value: `${formatCostRiskBps(range.low)} Bps` },
              { label: 'Sweep', value: `+${formatCostRiskBps(range.high)} Bps` },
              { label: 'Duration', value: `${formatDurationMinutes(band.durationMinutes)} min` },
            ],
          }),
        },
      };
    });

    const yCrossLines: CrossLine[] = [
      {
        type: 'line',
        value: 0,
        stroke: theme.colorTextSubtle,
        strokeWidth: 1,
        lineDash: [4, 4],
        label: {
          enabled: true,
          text: 'ARRIVAL',
          position: 'inside-right',
          color: theme.colorTextDefault,
          fontSize: 10,
          fontFamily: theme.fontFamily,
        },
      },
    ];

    return {
      animation: { enabled: false },
      // Transparent so the plot blends with the parent Card (no nested panel tint).
      background: { fill: 'transparent' },
      padding: { top: 8, right: 16, bottom: 24, left: 8 },
      series,
      axes: {
        x: {
          type: 'number' as const,
          position: 'bottom' as const,
          min: 0,
          max: xMax,
          title: { enabled: true, text: 'Duration (minutes)', ...titleStyle },
          label: {
            color: theme.colorTextSubtle,
            fontSize: 10,
            fontFamily: theme.fontFamily,
            formatter: ({ value }: { value: number }) => formatDurationMinutes(value),
          },
          gridLine: { style: [{ stroke: theme.borderColorChartGridLine, lineDash: [4, 4] }] },
          line: { stroke: theme.borderColorChartAxis },
        },
        y: {
          type: 'number' as const,
          position: 'left' as const,
          min: -(yAbs + yPad),
          max: yAbs + yPad,
          nice: true,
          title: { enabled: true, text: 'Slippage (Bps)', ...titleStyle },
          label: {
            color: theme.colorTextSubtle,
            fontSize: 10,
            fontFamily: theme.fontFamily,
            formatter: ({ value }: { value: number }) => formatCostRiskBps(value),
          },
          gridLine: { style: [{ stroke: theme.borderColorChartGridLine, lineDash: [4, 4] }] },
          line: { stroke: theme.borderColorChartAxis },
          crossLines: yCrossLines,
        },
      },
      legend: { enabled: false },
    };
  }, [bands, maxDurationMinutes, maxAbsBound, theme]);

  return (
    <ChartWrapper>
      <AgCharts options={chartOptions} />
    </ChartWrapper>
  );
}
